import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";

import { images } from "../../assets";
import { InsuredData } from "../../containers/forms/salud-pymes/salud-pymes-insured/salud-pymes-insured-types";
import { LeftInfoModalProps } from "../../components/left-info-modal/left-info-modal-layout";
import { UseImportInsuredsProps, UseImportInsuredsReturn } from "./useImportInsureds-types";
import { SimulationJsonData } from "../../containers/forms/salud-pymes/salud-pymes-insured/salud-pymes-insured-types";
import { HiringJsonData } from "../../containers/hirings/new-hiring/salud-pymes/salud-pymes-insured/salud-pymes-insured-types";

export const useImportInsureds = ({
  onAddInsureds,
  maxInsureds,
  coverages,
  validateInsureds,
  jsonDataType,
  countries,
  takerDocNumber,
}: UseImportInsuredsProps): UseImportInsuredsReturn => {
  const cleanModal: Partial<LeftInfoModalProps> = {
    img: "",
    className: "",
    mainTitle: "",
    mainText: "",
    list: [],
    buttonText: "",
    onClickAccept: () => {},
    active: false,
    noSecondaryButton: false,
    noMainButton: false,
    centerPrimaryButton: false,
    closeButtonText: "Cancelar",
  };

  const [modal, setModal] = useState<Partial<LeftInfoModalProps>>(cleanModal);
  const [loading, setLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = async (
    file: File
  ): Promise<{ errors: string[]; newInsureds: InsuredData[]; warnings?: string[] }> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = async (e) => {
        try {
          const readerTarget = e && e.target ? (e.target as FileReader) : null;
          const result =
            readerTarget && readerTarget.result ? (readerTarget.result as ArrayBuffer) : null;

          if (!result) {
            resolve({ errors: ["Error al leer el archivo"], newInsureds: [], warnings: [] });
            return;
          }

          const data = new Uint8Array(result);
          const workbook = XLSX.read(data, { type: "array", cellText: true, raw: true });

          const sheetName = workbook.SheetNames[0];
          if (!sheetName) {
            resolve({ errors: ["El archivo no contiene datos"], newInsureds: [], warnings: [] });
            return;
          }

          const worksheet = workbook.Sheets[sheetName];
          if (!worksheet) {
            resolve({ errors: ["No se pudo leer el archivo"], newInsureds: [], warnings: [] });
            return;
          }

          const jsonData =
            jsonDataType === "simulation"
              ? (XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as SimulationJsonData[])
              : (XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as HiringJsonData[]);

          const { errors, newInsureds, warnings } = await validateInsureds({
            jsonData,
            maxInsureds,
            coverages,
            countries,
            takerDocNumber: takerDocNumber || "",
          });

          resolve({ errors, newInsureds, warnings });
        } catch {
          reject({ errors: ["Archivo no válido"], newInsureds: [], warnings: [] });
        }
      };

      fileReader.onerror = () => {
        reject({ errors: ["Error al leer el archivo"], newInsureds: [], warnings: [] });
      };

      fileReader.readAsArrayBuffer(file);
    });
  };

  const handleSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length <= 0) return;
    const file = event.target.files[0];
    setLoading(true);

    try {
      const { errors, warnings, newInsureds } = await validateFile(file);
      setLoading(false);

      if (errors.length > 0) {
        setModal({
          img: images.IconWarningYellow,
          className: "error ghost-button",
          mainTitle: "La operación no se puede completar",
          list: errors,
          active: true,
          noMainButton: true,
          centerPrimaryButton: true,
          closeButtonText: "Cerrar",
        });
      } else {
        setModal({
          img: images.IconInfoBlue,
          className: "info",
          mainTitle: "Archivo correcto",
          mainText: `Esta acción borra los asegurados existentes y añade ${
            newInsureds && newInsureds.length
          } asegurados.`,
          list: warnings,
          buttonText: "Añadir Asegurados",
          onClickAccept: () => {
            onAddInsureds(newInsureds);
            setModal(cleanModal);
          },
          active: true,
        });
      }
    } catch {
      setModal({
        img: images.IconWarningYellow,
        className: "error ghost-button",
        mainTitle: "Error al procesar el archivo",
        list: ["Archivo no válido o corrupto"],
        active: true,
        noMainButton: true,
        centerPrimaryButton: true,
        closeButtonText: "Cerrar",
      });
      setLoading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetModal = () => setModal(cleanModal);

  return {
    loading,
    modal,
    fileInputRef,
    handleSelectFile,
    resetModal,
  };
};
